import { EditorOnly } from '../components/EditorOnly';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import type {
  PublishingBot,
  PublishingChannelView,
  PublishingSettings,
  PublishingCities,
  PublishingIntegrationView,
} from '../../../api/src/telegram/publishing.types';
import { Box, Stack, Typography } from '../components/mui-system';
import { api } from '../api/client';
import { TelegramBotPermissions } from './TelegramBotPermissions';
import { PublishingFiltersControl } from './TelegramPublishingFilters';
import { PUBLISHING_LIMITS } from '../../../api/src/telegram/publishing.types';
export function PublishingChannelForm({
  value,
  workspaceId,
  integration,
  bots,
  busy,
  error,
  onClose,
  onSave,
}: {
  value: PublishingChannelView | null;
  workspaceId: string;
  integration: PublishingIntegrationView;
  bots: PublishingBot[];
  busy: boolean;
  error: string;
  onClose: () => void;
  onSave: (input: PublishingSettings & { revision?: number }) => void;
}) {
  const [form, setForm] = useState<PublishingSettings>({
    title: value?.title ?? '',
    botId: value?.botId ?? bots[0]?.id ?? '',
    chatId: value?.chatId ?? '',
    cityId: value?.cityId ?? 0,
    sourceRevision: integration.revision,
    enabled: value?.enabled ?? false,
    dailyTarget: value?.dailyTarget ?? 10,
    repeatDays: value?.repeatDays ?? 7,
    startTime: value?.startTime ?? '09:00',
    endTime: value?.endTime ?? '21:00',
    filters: value?.filters ?? {},
    selectionOrder: value?.selectionOrder ?? 'sequential',
  });
  const sourceKey = `${integration.revision}:${integration.citiesVersion}`;
  const bot = bots.find((b) => b.id === form.botId);
  const [directory, setDirectory] = useState<{
    key: string;
    data?: PublishingCities;
    error?: string;
  } | null>(null);
  const requestId = useRef(0);
  const [reload, setReload] = useState(0);
  const force = useRef(false);
  useEffect(() => {
    const request = ++requestId.current;
    setDirectory(null);
    if (!integration.sourceUrl) return;
    const refresh = force.current;
    force.current = false;
    void api
      .publishingCities(workspaceId, refresh)
      .then((data) => {
        if (request !== requestId.current) return;
        if (data.revision !== integration.revision) {
          setDirectory({ key: sourceKey, error: 'Источник изменился. Обновите данные' });
          return;
        }
        setDirectory({ key: sourceKey, data });
      })
      .catch((e: unknown) => {
        if (request === requestId.current)
          setDirectory({
            key: sourceKey,
            error: e instanceof Error ? e.message : 'Не удалось загрузить города',
          });
      });
    return () => {
      requestId.current++;
    };
  }, [workspaceId, integration.revision, integration.sourceUrl, sourceKey, reload]);
  const current = directory?.key === sourceKey ? directory : null;
  const cities = current?.data?.items ?? [];
  const selected = cities.find((c) => c.id === form.cityId) ?? null;
  const available = !!selected?.timeZone;
  const missing = !!current?.data && !!form.cityId && !selected;
  const field = (
    key: keyof PublishingSettings,
    label: string,
    type = 'text',
    extra: Record<string, unknown> = {},
  ) => (
    <TextField
      key={key}
      label={label}
      type={type}
      required
      fullWidth
      value={form[key]}
      onChange={(e) =>
        setForm({ ...form, [key]: type === 'number' ? Number(e.target.value) : e.target.value })
      }
      {...extra}
    />
  );
  return (
    <Dialog open onClose={() => !busy && onClose()} fullWidth maxWidth="sm">
      <Box
        component="form"
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          if (!available || !current?.data) return;
          onSave({
            ...form,
            sourceRevision: current.data.revision,
            ...(value ? { revision: value.revision } : {}),
          });
        }}
      >
        <DialogTitle>{value ? 'Настройки канала' : 'Новый канал'}</DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            {!bots.length && (
              <Alert severity="info">Сначала закройте форму и подключите бота.</Alert>
            )}
            {field('title', 'Название канала')}
            <TextField
              label="Бот"
              select
              required
              value={form.botId}
              onChange={(e) => setForm({ ...form, botId: e.target.value })}
            >
              {bots.map((b) => (
                <MenuItem value={b.id} key={b.id}>
                  {b.name} · @{b.username}
                </MenuItem>
              ))}
            </TextField>
            {field('chatId', 'ID канала или @username', 'text', {
              disabled: !!value,
              helperText: 'Проверим канал и право бота публиковать сообщения.',
            })}
            <EditorOnly permissions="telegram.run">
              <TelegramBotPermissions
                key={JSON.stringify([workspaceId, bot?.id, bot?.revision, form.chatId.trim()])}
                workspaceId={workspaceId}
                botId={form.botId}
                chatId={form.chatId.trim()}
                disabled={busy || !bot}
              />
            </EditorOnly>
            <Divider />
            <Typography color="text.secondary">
              Источник: {integration.sourceUrl ?? 'не настроен'}
            </Typography>
            {!integration.sourceUrl ? (
              <Alert severity="info">Сначала настройте источник API.</Alert>
            ) : (
              <>
                {!current && <Typography role="status">Загружается справочник городов…</Typography>}
                {current?.error && (
                  <Alert severity="error">Не удалось загрузить справочник: {current.error}</Alert>
                )}
                {current?.data && !cities.length && (
                  <Alert severity="info">Справочник загружен, но городов нет.</Alert>
                )}
                {missing && (
                  <Alert severity="warning">
                    Город {value?.cityName ?? `#${form.cityId}`} ({value?.citySlug ?? form.cityId})
                    недоступен в справочнике. Выберите другой город.
                  </Alert>
                )}
                <Autocomplete
                  options={cities}
                  value={selected}
                  loading={!current}
                  disabled={busy || !current?.data}
                  getOptionLabel={(city) => `${city.name} · ${city.slug}`}
                  getOptionKey={(city) => city.id}
                  getOptionDisabled={(city) => !city.timeZone}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  onChange={(_, city) => setForm((f) => ({ ...f, cityId: city?.id ?? 0 }))}
                  noOptionsText="Города не найдены"
                  loadingText="Загружается справочник…"
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Город"
                      required
                      helperText="Поиск по названию или slug. Можно выбрать только значение из справочника."
                    />
                  )}
                  renderOption={(props, city) => (
                    <li {...props} key={city.id}>
                      {city.name} · {city.slug}
                      {!city.timeZone ? ' — недоступен: нет координат' : ''}
                    </li>
                  )}
                />
                <EditorOnly permissions="telegram.run">
                  <Button
                    disabled={busy || !current}
                    onClick={() => {
                      force.current = true;
                      setReload((n) => n + 1);
                    }}
                  >
                    {current?.error ? 'Повторить загрузку городов' : 'Обновить справочник городов'}
                  </Button>
                </EditorOnly>
              </>
            )}
            {value && (
              <Typography color="text.secondary">
                Текущий город: {value.cityName} · {value.timeZone}
              </Typography>
            )}
            <PublishingFiltersControl
              key={JSON.stringify([workspaceId, integration.revision, form.cityId])}
              workspaceId={workspaceId}
              sourceRevision={integration.revision}
              cityId={form.cityId}
              disabled={busy || !available}
              value={form.filters ?? {}}
              selectionOrder={form.selectionOrder ?? 'sequential'}
              onChange={(filters, selectionOrder) =>
                setForm((f) => ({ ...f, filters, selectionOrder }))
              }
            />
            <Stack direction="row" gap={2}>
              {field('startTime', 'Начало', 'time', {
                slotProps: { inputLabel: { shrink: true } },
              })}
              {field('endTime', 'Конец', 'time', { slotProps: { inputLabel: { shrink: true } } })}
            </Stack>
            <Typography color="text.secondary" fontSize={12}>
              Время города, в пределах одного дня. Минимальный интервал — 5 минут.
            </Typography>
            {field('dailyTarget', 'Публикаций в день', 'number', {
              slotProps: {
                htmlInput: { min: PUBLISHING_LIMITS.dailyMin, max: PUBLISHING_LIMITS.dailyMax },
              },
              helperText: `От ${PUBLISHING_LIMITS.dailyMin} до ${PUBLISHING_LIMITS.dailyMax} публикаций в день.`,
            })}
            {field('repeatDays', 'Повтор не раньше, чем через (суток)', 'number', {
              slotProps: {
                htmlInput: { min: PUBLISHING_LIMITS.repeatMin, max: PUBLISHING_LIMITS.repeatMax },
              },
              helperText: `От ${PUBLISHING_LIMITS.repeatMin} до ${PUBLISHING_LIMITS.repeatMax} суток; 7 — значение по умолчанию.`,
            })}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={onClose}>
            Отмена
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !bots.length || !available || !current?.data}
          >
            {busy ? 'Проверка…' : 'Сохранить'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
